import React, { memo } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Sparkles, Check, Eye } from "lucide-react";
import NCAAService from "../../../../../components/dashboards/services/ncaaService";
import { useAddToChat } from "../../../../../components/chat/chatContext";
import XPicksButton from "../../../../../components/dashboards/components/shared/XPicksButton";
import PredictButton from "../../../../../components/dashboards/components/shared/PredictButton";
import ViewOddsButton from "../../../../../components/dashboards/components/shared/ViewOddsButton";
import PredictionBox from "../../../../../components/dashboards/components/shared/PredictionBox";

const NCAAFMobileGameCard = memo(
  ({
    game,
    isSelected,
    isFrozen = false,
    onToggleSelection,
    prediction,
    showPredictionButton = true,
    onViewOdds,
    onViewPrediction
  }) => {
    const navigate = useNavigate();
    const handleAddToChat = useAddToChat();

    const handleAnalyzeInChat = async () => {
      if (!prediction) return;
      const { teams } = game;
      const message = `Analyze this NCAAF prediction:

${teams?.away?.name} @ ${teams?.home?.name}

Prediction: ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% confidence)` : ""}
Confidence Level: ${prediction.confidence}
${prediction.reason ? `Reasoning: ${prediction.reason}` : ""}

Can you provide a deeper analysis of this matchup?`;

      await handleAddToChat(message);
      navigate("/chat");
    };

    if (!game) return null;

    const { teams, scores, game: gameInfo } = game;
    const statusShort = gameInfo?.status?.short;
    const isLive =
      statusShort === "IN PLAY" ||
      statusShort === "LIVE" ||
      statusShort === "HT" ||
      (statusShort && statusShort.startsWith("Q")) ||
      statusShort === "OT";
    const isFinished = statusShort === "FT" || statusShort === "AOT";
    const isUpcoming = !isLive && !isFinished;

    const formatGameTime = () => {
      if (isLive) return "LIVE";
      if (isFinished) return "Final";

      try {
        if (gameInfo?.date?.date && gameInfo?.date?.time) {
          const dateTimeString = `${gameInfo.date.date}T${gameInfo.date.time}`;
          return NCAAService.formatTime(dateTimeString);
        } else if (gameInfo?.date) {
          return NCAAService.formatTime(gameInfo.date);
        }
      } catch (e) {
        return "TBD";
      }

      return "TBD";
    };

    const getQuarterDisplay = () => {
      if (!isLive) return null;
      if (statusShort && statusShort.startsWith("Q")) return statusShort;
      if (statusShort === "HT") return "HT";
      if (statusShort === "OT") return "OT";
      const quarter = gameInfo?.status?.quarter;
      if (quarter) return `Q${quarter}`;
      return "In Progress";
    };

    const getRanking = (teamName) => {
      const match = teamName?.match(/^#(\d+)\s/);
      return match ? match[1] : null;
    };

    const cleanTeamName = (teamName) => teamName?.replace(/^#\d+\s/, "") || "Unknown";
    const effectiveFrozen = isFrozen || Boolean(prediction);

    return (
      <div
        className="relative border border-white/10 p-4 bg-white/[0.03]"
      >
        {(isLive || isFinished) && (
          <div className="absolute top-2 right-2 z-10 mt-1">
            <div
              className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
                isLive ? "bg-red-500" : "bg-white/20"
              }`}
            >
              {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
              <span className="text-[10px] font-bold text-white">
                {isLive ? (getQuarterDisplay() ? `LIVE ${getQuarterDisplay()}` : "LIVE") : "FINAL"}
              </span>
            </div>
          </div>
        )}

        <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1.5 truncate pr-16">
          {teams?.home?.conference || teams?.away?.conference || "NCAA"}
        </div>

        <div className="space-y-2 mt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {teams?.away?.logo && (
                <img
                  src={teams.away.logo}
                  alt={teams.away.name}
                  className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40"
                  loading="lazy"
                />
              )}
              <div className="flex items-center gap-1 flex-1 min-w-0">
                {getRanking(teams?.away?.name) && (
                  <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-1 py-0.5 rounded flex-shrink-0">
                    #{getRanking(teams?.away?.name)}
                  </span>
                )}
                <span className="text-sm font-mono text-white tracking-tight leading-tight">
                  {cleanTeamName(teams?.away?.name) || "Away Team"}
                </span>
              </div>
            </div>
            <span className="text-xl font-black text-white ml-2 tabular-nums">
              {isFinished || isLive ? scores?.away?.total ?? scores?.away ?? "-" : "-"}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {teams?.home?.logo && (
                <img
                  src={teams.home.logo}
                  alt={teams.home.name}
                  className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40"
                  loading="lazy"
                />
              )}
              <div className="flex items-center gap-1 flex-1 min-w-0">
                {getRanking(teams?.home?.name) && (
                  <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-1 py-0.5 rounded flex-shrink-0">
                    #{getRanking(teams?.home?.name)}
                  </span>
                )}
                <span className="text-sm font-mono text-white tracking-tight leading-tight">
                  {cleanTeamName(teams?.home?.name) || "Home Team"}
                </span>
              </div>
            </div>
            <span className="text-xl font-black text-white ml-2 tabular-nums">
              {isFinished || isLive ? scores?.home?.total ?? scores?.home ?? "-" : "-"}
            </span>
          </div>
        </div>

        {!isFinished && !isLive && (
          <div className="mt-2 pt-1.5 border-t border-white/10">
            <div className="flex items-center justify-center gap-1 text-white/50">
              <Clock size={9} />
              <span className="text-[10px] font-medium">
                {formatGameTime()}
              </span>
            </div>
          </div>
        )}

        {prediction && (
          <div className="pt-2">
            <PredictionBox
              prediction={prediction}
              onAnalyze={handleAnalyzeInChat}
              variant="mobile"
            />
          </div>
        )}

        <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
          {(isUpcoming || isLive) && (
            <div className="col-span-1">
              <XPicksButton
                gameData={{
                  homeTeam: teams?.home?.name,
                  awayTeam: teams?.away?.name,
                  homeTeamId: teams?.home?.id,
                  awayTeamId: teams?.away?.id,
                  leagueId: game?.game?.league?.id || 2,
                  date: gameInfo?.date?.date || gameInfo?.date,
                  time: formatGameTime(),
                  gameId: game?.game?.id
                }}
                sport="ncaaf"
                label="XPICKS"
              />
            </div>
          )}

          {showPredictionButton && (isUpcoming || isLive) && onToggleSelection && (
            <div className="col-span-1">
              <PredictButton
                label={effectiveFrozen ? "COOLDOWN" : isSelected ? "Selected" : isLive ? "Predict Live" : "Predict"}
                icon={effectiveFrozen ? Clock : isSelected ? Check : Sparkles}
                isSelected={isSelected}
                isLive={isLive}
                isFrozen={effectiveFrozen}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!effectiveFrozen) {
                    onToggleSelection(game);
                    if (onViewPrediction && !isSelected) {
                      onViewPrediction(game);
                    }
                  }
                }}
              />
            </div>
          )}

          {onViewOdds && (
            <div className="col-span-1">
              <ViewOddsButton
                label="View Odds"
                onClick={() => onViewOdds(game)}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

NCAAFMobileGameCard.displayName = "MobileNCAAFGameCard";

export default NCAAFMobileGameCard;
